/**
 * ProjectSuggestionDialog — shown after a user picks a calendar event
 * whose subject fuzzy-matches an existing project name. Gives them an
 * explicit "yes this is the right project" confirmation before the
 * draft is created, because the match can misfire on ambiguous names.
 *
 * Closed states:
 *   - "Use this project"   → onAccept(projectId) — prefills the form
 *   - "Pick manually"      → onDismiss() — user picks via the form's
 *                            ProjectPicker instead
 *   - Click outside / ✕   → same as "Pick manually"
 */
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/Modal.tsx';

interface Props {
  isOpen: boolean;
  subject: string;
  suggestion: { id: string; name: string; similarity: number } | null;
  onAccept: (projectId: string, projectName: string) => void;
  onDismiss: () => void;
}

export function ProjectSuggestionDialog({
  isOpen,
  subject,
  suggestion,
  onAccept,
  onDismiss,
}: Props) {
  if (!isOpen || !suggestion) return null;

  const pct = Math.round(suggestion.similarity * 100);
  const confidenceLabel = pct >= 60 ? 'High' : pct >= 40 ? 'Medium' : 'Low';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onDismiss}
      size="md"
      ariaLabel="Confirm project from calendar subject"
    >
      <ModalHeader
        title="Looks like a project match"
        subtitle="Calendar subjects don't always match OJB's project naming convention. Double-check before we create the draft."
        onClose={onDismiss}
      />

      <ModalBody>
        <div className="mb-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-charcoal/60">
            From calendar subject
          </div>
          <div className="mt-1 text-sm text-charcoal">
            &ldquo;{subject}&rdquo;
          </div>
        </div>

        <div className="rounded-2xl border border-pine/25 bg-pine/5 px-4 py-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-pine">
            Suggested project · {confidenceLabel} confidence ({pct}% title
            overlap)
          </div>
          <div className="mt-1 text-sm font-medium text-charcoal">
            {suggestion.name}
          </div>
        </div>

        <p className="mt-4 text-[11px] text-charcoal/60">
          Use this suggestion if it&rsquo;s correct. If the meeting belongs
          to a different project, pick it manually on the form.
        </p>
      </ModalBody>

      <ModalFooter className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-full border border-charcoal/25 px-4 py-2 text-sm text-charcoal transition-colors hover:border-pine hover:text-pine"
        >
          Pick manually
        </button>
        <button
          type="button"
          onClick={() => onAccept(suggestion.id, suggestion.name)}
          className="rounded-full bg-pine px-4 py-2 text-sm font-semibold text-cloud transition-colors hover:bg-pine/90"
        >
          Use {truncate(suggestion.name, 28)}
        </button>
      </ModalFooter>
    </Modal>
  );
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}
